import dayjs from "dayjs";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";

const ORDER_DOCUMENT_PATH_PREFIX = "/my-account/documents/";
const QUOTE_DOCUMENT_PATH_PREFIX = "/my-account/quotes/documents/";

export function getCsrfFieldName() {
  return "CSRFToken";
}

export function getCsrfValue() {
  return window.__SCHA_BOOT__?.csrf ?? "";
}

export function getCsrfFormParamName() {
  return "CSRFToken";
}

export function format(date: Date, formatString = "YYYY-MM-DD") {
  return dayjs(date).format(formatString);
}

export function formatNullable(date: Date | undefined | null, formatString = "YYYY-MM-DD") {
  return date ? format(date, formatString) : "";
}

export function getDate(val: null): undefined;
export function getDate(val: string | number): Date;
export function getDate(val: string | null): Date | undefined;
export function getDate(val: number | null): Date | undefined;
export function getDate(val: string | number | null): Date | undefined {
  if (!val) {
    return undefined;
  }

  if (typeof val === "number") {
    return new Date(val);
  }

  return dayjs(val, "YYYY-MM-DD").toDate();
}

export function submitAsForm(
  data: Dict<string | number | boolean | undefined>,
  action: string,
  method: string = "POST",
) {
  const form = document.createElement("form");
  form.method = method;
  form.action = action;
  form.hidden = true;
  for (const [key, value] of Object.entries(data)) {
    if (!value) {
      continue;
    }

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value.toString();
    form.appendChild(input);
  }
  // CSRF token
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = getCsrfFieldName();
  input.value = getCsrfValue();
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
}

export function downloadBlob(filename: string) {
  return (blob: Blob) => {
    if (navigator.appVersion.toString().indexOf(".NET") > 0) {
      // @ts-ignore
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    }
  };
}

export async function downloadBlobFromResponse(response: Response) {
  const filename = response.headers.get("Content-Disposition")!.split("filename=")[1].replace(/"/g, "");
  const blob = await response.blob();
  downloadBlob(filename)(blob);
}

export const downloadFile = (url: string): Promise<unknown> => {
  const parsedUrl = new URL(url, window.location.origin);
  const isOrderDocument = parsedUrl.pathname.startsWith(ORDER_DOCUMENT_PATH_PREFIX);
  const isQuoteDocument = parsedUrl.pathname.startsWith(QUOTE_DOCUMENT_PATH_PREFIX);
  const isOccDocument = isOrderDocument || isQuoteDocument;
  const downloadUrl = isOccDocument ? toDocumentOccUrl(parsedUrl, isQuoteDocument) : url;
  const request = isOccDocument ? occFetch : fetch;

  return request(downloadUrl, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  }).then(downloadBlobFromResponse);
};

function toDocumentOccUrl(url: URL, isQuoteDocument: boolean): string {
  const pathPrefix = isQuoteDocument ? QUOTE_DOCUMENT_PATH_PREFIX : ORDER_DOCUMENT_PATH_PREFIX;
  const occPath = isQuoteDocument ? "/quotes/documents" : "/orders/documents";
  const params = new URLSearchParams(url.search);
  params.set("documentPath", decodeURIComponent(url.pathname.substring(pathPrefix.length)));
  return `${occUrl(occPath)}?${params.toString()}`;
}

export const downloadAssetFile = async (url: string, fileName?: string) => {
  const response = await fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }

  const parts = new URL(url).pathname.split("/");
  const filename = fileName || parts[parts.length - 1];

  const blob = await response.blob();
  downloadBlob(filename)(blob);
};
